import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Target, Lock, MailCheck, Send } from "lucide-react";
import { matrixStrings as ms, INCOME_MID, VYDAJE_MID, RATE_MID, MARKET_RATE } from "../../lib/matrixStrings";
import { SmileLevel, LEVEL_COLORS } from "./SmileLevel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = ["pojisteni", "investice", "sporeni", "hypoteky", "uvery"];
const BAND_VALUES = { critical: [3000, 8000], high: [1500, 4000], tune: [500, 1500] };
const RATE_LEVEL = { lt: 4, m1: 3, m2: 1, gt: 0, unknown: 2 };
const RATE_KEYS = ["lt", "m1", "m2", "gt", "unknown"];

const bandOf = (level) => (level === 0 ? "critical" : level <= 2 ? "high" : "tune");
const BAND_BORDER = {
  critical: "border-red-400/40",
  high: "border-amber-400/40",
  tune: "border-teal-400/30",
};

const verdictIdxOf = (index) => (index < 20 ? 0 : index < 40 ? 1 : index < 60 ? 2 : index < 80 ? 3 : 4);
const VERDICT_COLOR = ["#2DD4BF", "#2DD4BF", "#FBBF24", "#F87171", "#F87171"];

export const MatrixAnalysis = ({ lang, scores, income = null, vydaje = null, incomeExact = null, vydajeExact = null, vek = null, rates = null, hasHypo = null, hasUver = null, loanDetails = null, answers = null, name, contact = null, autoSend = false, onResultsSent, onContact, onBook, onBack }) => {
  const [logCount, setLogCount] = useState(0);
  const logs = ms.analysis.logs[lang];
  const done = logCount >= logs.length;

  useEffect(() => {
    if (done) return;
    const t = setTimeout(() => setLogCount((c) => c + 1), 620);
    return () => clearTimeout(t);
  }, [logCount, done, logs.length]);

  const fmt = (n) => new Intl.NumberFormat(lang === "cs" ? "cs-CZ" : "en-GB").format(Math.round(n));

  const { index, ordered, avg } = useMemo(() => {
    const avgS = PILLARS.reduce((s, p) => s + scores[p], 0) / PILLARS.length;
    const idx = Math.min(97, Math.max(5, Math.round((1 - avgS / 4) * 100)));
    const ord = [...PILLARS].sort((a, b) => scores[a] - scores[b]);
    return { index: idx, ordered: ord, avg: avgS };
  }, [scores]);

  const noneOf = (p) => (p === "hypoteky" ? hasHypo === false : p === "uvery" ? hasUver === false : false);
  const totalLow = PILLARS.reduce((s, p) => (noneOf(p) ? s : s + BAND_VALUES[bandOf(scores[p])][0]), 0);
  const totalHigh = PILLARS.reduce((s, p) => (noneOf(p) ? s : s + BAND_VALUES[bandOf(scores[p])][1]), 0);
  const inc = incomeExact ?? (income !== null ? INCOME_MID[income] : null);
  const exp = vydajeExact ?? (vydaje !== null ? VYDAJE_MID[vydaje] : null);
  const free = inc !== null && exp !== null ? inc - exp : null;
  const pct = free !== null && inc > 0 ? free / inc : null;
  const cashBand = pct === null ? (avg <= 1.5 ? "tight" : avg <= 2.8 ? "ok" : "strong") : pct <= 0 ? "deficit" : pct < 0.1 ? "tight" : pct < 0.2 ? "ok" : "strong";

  const vIdx = verdictIdxOf(index);
  const vColor = VERDICT_COLOR[vIdx];
  const R = 58;
  const CIRC = 2 * Math.PI * R;

  const rateRows = [];
  if (rates?.hypo) rateRows.push({ key: "hypo", rateKey: rates.hypo, market: ms.analysis.marketHypo[lang], label: ms.pillars.hypoteky[lang] });
  if (rates?.uver) rateRows.push({ key: "uver", rateKey: rates.uver, market: ms.analysis.marketUver[lang], label: ms.pillars.uvery[lang] });
  const verdict = (rk) => {
    const lv = RATE_LEVEL[rk];
    return lv >= 4 ? ms.analysis.rateGood[lang] : lv === 3 ? ms.analysis.rateOk[lang] : lv === 2 ? ms.analysis.rateUnknown[lang] : ms.analysis.rateBad[lang];
  };
  const rateLabel = (row) => {
    const i = RATE_KEYS.indexOf(row.rateKey);
    return (row.key === "hypo" ? ms.discovery.hypoteky.rates[lang] : ms.discovery.uvery.rates[lang])[i];
  };
  const pmt = (P, rAnnual, years) => {
    if (!P || !years) return null;
    const i = rAnnual / 100 / 12;
    const n = years * 12;
    if (!i) return P / n;
    return (P * i) / (1 - Math.pow(1 + i, -n));
  };
  const loanCalc = (key) => {
    const d = loanDetails?.[key];
    if (!d?.pay || !d?.bal || !d?.years) return null;
    const market = MARKET_RATE[key];
    const marketPay = pmt(d.bal, market, d.years);
    const totalOwn = d.pay * d.years * 12;
    const totalMarket = marketPay * d.years * 12;
    return { ...d, market, marketPay, savings: Math.max(0, totalOwn - totalMarket) };
  };
  const tipFor = (p, band) => {
    if (noneOf(p)) return ms.pillarTips[p].none[lang];
    if (p === "hypoteky" || p === "uvery") {
      const rk = p === "hypoteky" ? rates?.hypo : rates?.uver;
      if (rk) {
        const lv = RATE_LEVEL[rk];
        const b = lv >= 3 ? "tune" : lv === 2 ? "high" : "critical";
        return ms.pillarTips[p][b][lang];
      }
    }
    return ms.pillarTips[p][band][lang];
  };

  // --- results email ---
  const [mailName, setMailName] = useState(contact?.name || "");
  const [mailEmail, setMailEmail] = useState(contact?.email || "");
  const [mailState, setMailState] = useState("idle");
  const mailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mailEmail.trim());

  const buildRecap = () => {
    const d = ms.discovery;
    const rows = [];
    if (inc !== null) rows.push({ label: d.prijem.label[lang], value: incomeExact ? `~${fmt(incomeExact)} Kč` : d.prijem.amounts[lang][income] });
    if (exp !== null) rows.push({ label: d.vydaje.label[lang], value: vydajeExact ? `~${fmt(vydajeExact)} Kč` : d.vydaje.amounts[lang][vydaje] });
    if (vek !== null) rows.push({ label: d.vek.label[lang], value: d.vek.amounts[lang][vek] });
    if (answers?.ins?.length) rows.push({ label: d.pojisteni.label[lang], value: answers.ins.map((k) => d.pojisteni.options[k]?.[lang] || k).join(", ") });
    if (answers && answers.invests !== null && answers.invests !== undefined) {
      const v = answers.invests
        ? answers.how === "pravidelne"
          ? `${d.investice.how.pravidelne[lang]} – ${d.investice.amounts[lang][answers.amount]}`
          : `${d.investice.how.jednorazove[lang]} – ${fmt(Number(String(answers.lump).replace(/[^0-9]/g, "")) || 0)} Kč`
        : d.investice.no[lang];
      rows.push({ label: d.investice.label[lang], value: v });
    }
    if (answers?.sav?.length) rows.push({ label: d.sporeni.label[lang], value: answers.sav.map((k) => d.sporeni.options[k]?.[lang] || k).join(", ") });
    const noneTxt = lang === "cs" ? "Nemá" : "None";
    const loanRow = (key, label, has) => {
      if (!has) return { label, value: noneTxt };
      const det = loanDetails?.[key];
      const rateTxt = rates?.[key] ? rateLabel({ key, rateKey: rates[key] }) : "";
      const parts = [];
      if (rateTxt) parts.push(rateTxt);
      if (det?.pay) parts.push(`${ms.analysis.loanMonthly[lang]} ${fmt(det.pay)} Kč`);
      if (det?.bal) parts.push(`${ms.analysis.loanBalance[lang]} ${fmt(det.bal)} Kč`);
      if (det?.years) parts.push(`${det.years} ${ms.analysis.loanYears[lang]}`);
      return { label, value: parts.join(" · ") || "—" };
    };
    if (hasHypo !== null) rows.push(loanRow("hypo", d.hypoteky.label[lang], hasHypo));
    if (hasUver !== null) rows.push(loanRow("uver", d.uvery.label[lang], hasUver));
    const tips = PILLARS.map((p) => ({ label: ms.pillarFull[p][lang], value: tipFor(p, bandOf(scores[p])) }));
    const targets = [];
    if (exp !== null) targets.push({ label: ms.analysis.targetReserve[lang], value: `${fmt(exp * 3)}–${fmt(exp * 6)} Kč` });
    if (inc !== null) targets.push({ label: ms.analysis.targetInvest[lang], value: `${fmt(inc * 0.1)}–${fmt(inc * 0.15)} ${ms.analysis.monthly[lang]}` });
    const cashflow = free !== null ? `${fmt(free)} Kč/měs. · ${ms.analysis.cashflowBands[cashBand][lang]}` : "";
    return { rows, tips, targets, cashflow };
  };

  const sendResults = async (emailOverride = null) => {
    const targetEmail = (emailOverride || mailEmail).trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(targetEmail) || mailState === "sending") return;
    setMailState("sending");
    try {
      const { rows, tips, targets, cashflow } = buildRecap();
      await axios.post(`${API}/matrix/results-email`, {
        name: mailName.trim(),
        email: targetEmail,
        phone: contact?.phone || "",
        lang,
        index,
        verdict: ms.analysis.verdictTitle[lang][vIdx],
        rows,
        tips,
        targets,
        cashflow,
      });
      setMailState("done");
      onContact?.(mailName.trim(), targetEmail);
      onResultsSent?.();
    } catch {
      setMailState("error");
    }
  };

  const autoSentRef = useRef(false);
  useEffect(() => {
    if (autoSend && contact?.email && !autoSentRef.current) {
      autoSentRef.current = true;
      setMailEmail(contact.email);
      // no cleanup — a StrictMode dev remount must not cancel the scheduled send;
      // the ref guard prevents duplicates
      setTimeout(() => sendResults(contact.email), 1500);
    }
    return undefined;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto max-w-3xl"
      data-testid="matrix-analysis"
    >
      <p className="text-center font-mono text-[11px] font-semibold tracking-[0.3em] text-teal-400">{ms.analysis.kicker[lang]}</p>

      <div className="mg-glass mt-6 rounded-2xl p-6 font-mono text-xs leading-loose text-teal-200/90 sm:text-sm" data-testid="analysis-logs">
        {logs.slice(0, logCount).map((l, i) => (
          <motion.p key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}>
            <span className="text-teal-500">&gt;</span> {l} <span className="text-teal-400">OK</span>
          </motion.p>
        ))}
        {!done && (
          <p>
            <span className="text-teal-500">&gt;</span> <span className="mg-blink">█</span>
          </p>
        )}
      </div>

      {done && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          {/* --- VERDICT --- */}
          <div className="mg-glass mt-10 rounded-2xl p-7 sm:p-9" data-testid="analysis-verdict">
            <div className="flex flex-col items-center gap-8 sm:flex-row">
              <div className="relative h-36 w-36 shrink-0">
                <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
                  <circle cx="70" cy="70" r={R} fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth="11" />
                  <motion.circle
                    cx="70" cy="70" r={R} fill="none"
                    stroke={vColor}
                    strokeWidth="11"
                    strokeLinecap="round"
                    strokeDasharray={`${(index / 100) * CIRC} ${CIRC}`}
                    initial={{ strokeDasharray: `0 ${CIRC}` }}
                    animate={{ strokeDasharray: `${(index / 100) * CIRC} ${CIRC}` }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    style={{ filter: `drop-shadow(0 0 10px ${vColor}66)` }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-mono text-4xl font-extrabold tabular-nums text-white" data-testid="vulnerability-index">{index}</span>
                  <span className="font-mono text-[10px] text-slate-500">/100</span>
                </div>
              </div>
              <div className="text-center sm:text-left">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-teal-400">{ms.analysis.indexLabel[lang]}</p>
                <h2 className="font-heading mt-2 text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="verdict-title">
                  {ms.analysis.verdictTitle[lang][vIdx]}
                </h2>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-400">{ms.analysis.verdictSub[lang][vIdx]}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">{ms.analysis.verdictScale[lang]}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                  {PILLARS.map((p) => (
                    <span key={p} className="flex items-center gap-1.5 text-xs text-slate-300" data-testid={`score-smile-${p}`}>
                      <SmileLevel level={scores[p]} size={15} />
                      {ms.pillars[p][lang]}
                    </span>
                  ))}
                </div>
                {name ? <p className="mt-2 text-xs text-slate-500">{name}</p> : null}
              </div>
            </div>
          </div>

          {/* --- PLAIN-LANGUAGE PILLAR CARDS --- */}
          <h3 className="font-heading mt-10 text-xl font-bold text-white">{ms.analysis.pillarsHeading[lang]}</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {ordered.map((p, i) => {
              const band = bandOf(scores[p]);
              return (
                <motion.div
                  key={p}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`mg-glass rounded-xl border-l-4 p-5 ${BAND_BORDER[band]}`}
                  data-testid={`pillar-tip-${p}`}
                >
                  <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-300">
                    <SmileLevel level={scores[p]} size={18} />
                    {ms.pillarFull[p][lang]}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{tipFor(p, band)}</p>
                </motion.div>
              );
            })}
          </div>

          {/* --- RATES VS MARKET --- */}
          {rateRows.length > 0 && (
            <div className="mg-glass mt-6 rounded-2xl p-6 sm:p-7" data-testid="rate-comparison">
              <h3 className="font-heading text-lg font-bold text-white">{ms.analysis.rateHeading[lang]}</h3>
              <div className="mt-4 space-y-4">
                {rateRows.map((row) => {
                  const lc = loanCalc(row.key);
                  return (
                    <div key={row.key} className="rounded-xl border border-teal-400/15 bg-white/[0.03] p-4" data-testid={`rate-row-${row.key}`}>
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-400">{row.label}</p>
                          <p className="mt-0.5 font-mono text-sm font-semibold text-white">{rateLabel(row)}</p>
                          <p className="mt-0.5 text-[11px] text-slate-500">{row.market}</p>
                        </div>
                        <div className="flex items-center gap-2 text-right">
                          <SmileLevel level={RATE_LEVEL[row.rateKey]} size={26} testid={`rate-smile-${row.key}`} />
                          <span className="max-w-[180px] text-xs font-medium" style={{ color: LEVEL_COLORS[RATE_LEVEL[row.rateKey]] }}>
                            {verdict(row.rateKey)}
                          </span>
                        </div>
                      </div>
                      {lc && (
                        <div className="mt-3 border-t border-teal-400/10 pt-3" data-testid={`loan-detail-${row.key}`}>
                          <p className="font-mono text-xs text-slate-300">
                            {ms.analysis.loanMonthly[lang]} <span className="font-bold text-white">{fmt(lc.pay)} Kč</span>
                            {" · "}{ms.analysis.loanBalance[lang]} <span className="font-bold text-white">{fmt(lc.bal)} Kč</span>
                            {" · "}{lc.years} {ms.analysis.loanYears[lang]}
                          </p>
                          <p className="mt-1.5 font-mono text-xs text-slate-300" data-testid={`loan-market-pay-${row.key}`}>
                            {ms.analysis.loanMarketPay[lang]} <span className="font-bold text-white">~{fmt(lc.marketPay)} Kč</span>
                          </p>
                          {lc.savings > 500 && (
                            <p className="mt-1.5 font-mono text-xs font-bold text-teal-300" data-testid={`loan-savings-${row.key}`}>
                              {ms.analysis.loanSavings[lang]} ~{fmt(lc.savings)} Kč
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* --- RECOMMENDED TARGETS --- */}
          {(inc !== null || exp !== null) && (
            <div className="mg-glass mt-6 rounded-2xl p-6 sm:p-7" data-testid="targets-card">
              <h3 className="font-heading flex items-center gap-2 text-lg font-bold text-white">
                <Target size={18} className="text-teal-300" />
                {ms.analysis.targetsHeading[lang]}
              </h3>
              <div className="mt-4 space-y-3">
                {exp !== null && (
                  <div className="flex items-center justify-between gap-4" data-testid="target-reserve">
                    <div>
                      <p className="text-sm font-semibold text-slate-200">{ms.analysis.targetReserve[lang]}</p>
                      <p className="text-[11px] text-slate-500">{ms.analysis.targetReserveHint[lang]}</p>
                    </div>
                    <span className="font-mono text-sm font-bold text-teal-300">{fmt(exp * 3)}–{fmt(exp * 6)} Kč</span>
                  </div>
                )}
                {inc !== null && (
                  <div className="flex items-center justify-between gap-4" data-testid="target-invest">
                    <div>
                      <p className="text-sm font-semibold text-slate-200">{ms.analysis.targetInvest[lang]}</p>
                      <p className="text-[11px] text-slate-500">{ms.analysis.targetInvestHint[lang]}</p>
                    </div>
                    <span className="font-mono text-sm font-bold text-teal-300">{fmt(inc * 0.1)}–{fmt(inc * 0.15)} {ms.analysis.monthly[lang]}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-4 border-t border-teal-400/15 pt-3">
                  <span className="text-sm text-slate-300">{ms.analysis.cashflowLabel[lang]}</span>
                  <span className={`font-mono text-xs font-semibold ${cashBand === "deficit" ? "text-red-300" : "text-slate-300"}`} data-testid="cashflow-band">
                    {free !== null && <span className="mr-1">{free < 0 ? `${fmt(free)} Kč/měs.` : `~${fmt(free)} Kč/měs.`} ·</span>}
                    {ms.analysis.cashflowBands[cashBand][lang]}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* --- IMPACT RANGES --- */}
          <div className="mg-glass mt-6 rounded-2xl p-6 sm:p-7" data-testid="impact-ranges">
            <h3 className="font-heading text-lg font-bold text-white">{ms.analysis.impactHeading[lang]}</h3>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{ms.analysis.impactSub[lang]}</p>
            <div className="mt-5 space-y-3">
              {ordered.map((p) => (
                <div key={p} className="flex items-center justify-between gap-4" data-testid={`impact-${p}`}>
                  <span className="flex items-center gap-2 text-sm text-slate-300">
                    <SmileLevel level={scores[p]} size={16} />
                    {ms.pillarFull[p][lang]}
                  </span>
                  <span className="font-mono text-sm font-semibold text-teal-300">
                    {noneOf(p) ? ms.analysis.impactNone[lang] : `~${ms.impactRanges[bandOf(scores[p])][lang]} ${ms.analysis.impactPerMonth[lang]}`}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between gap-4 border-t border-teal-400/15 pt-3">
                <span className="text-sm font-semibold text-white">{ms.analysis.totalLabel[lang]}</span>
                <span className="font-mono text-sm font-bold text-white" data-testid="impact-total">
                  {fmt(totalLow)}–{fmt(totalHigh)} {ms.analysis.impactPerMonth[lang]}
                </span>
              </div>
              {(income !== null || incomeExact) && (
                <div className="flex items-center justify-between gap-4" data-testid="impact-income">
                  <span className="text-sm text-slate-300">{ms.discovery.prijem.label[lang]}</span>
                  <span className="font-mono text-sm font-semibold text-slate-200">
                    {incomeExact ? `~${fmt(incomeExact)} Kč` : ms.discovery.prijem.amounts[lang][income]}
                  </span>
                </div>
              )}
              {(vydaje !== null || vydajeExact) && (
                <div className="flex items-center justify-between gap-4" data-testid="impact-expenses">
                  <span className="text-sm text-slate-300">{ms.discovery.vydaje.label[lang]}</span>
                  <span className="font-mono text-sm font-semibold text-slate-200">
                    {vydajeExact ? `~${fmt(vydajeExact)} Kč` : ms.discovery.vydaje.amounts[lang][vydaje]}
                  </span>
                </div>
              )}
              {vek !== null && (
                <div className="flex items-center justify-between gap-4" data-testid="impact-age">
                  <span className="text-sm text-slate-300">{ms.discovery.vek.label[lang]}</span>
                  <span className="font-mono text-sm font-semibold text-slate-200">{ms.discovery.vek.amounts[lang][vek]}</span>
                </div>
              )}
            </div>
            <p className="mt-4 text-[11px] leading-relaxed text-slate-500">{ms.analysis.rangeDisclaimer[lang]}</p>
          </div>

          {/* --- ACTION MATRIX --- */}
          <h3 className="font-heading mt-10 text-xl font-bold text-white">{ms.analysis.actionsHeading[lang]}</h3>
          <div className="mt-4 space-y-3" data-testid="action-matrix">
            {ordered.map((p, i) => {
              const band = bandOf(scores[p]);
              const bandCls = {
                critical: "border-red-400/40 bg-red-400/10 text-red-300",
                high: "border-amber-400/40 bg-amber-400/10 text-amber-300",
                tune: "border-teal-400/40 bg-teal-400/10 text-teal-300",
              };
              return (
                <motion.div
                  key={p}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.12 }}
                  className="mg-glass flex items-start gap-4 rounded-xl p-5"
                  data-testid={`action-${p}`}
                >
                  <span className={`shrink-0 rounded-full border px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${bandCls[band]}`}>
                    {ms.analysis.bands[band][lang]}
                  </span>
                  <div className="flex-1">
                    <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-400">
                      <SmileLevel level={scores[p]} size={14} />
                      {ms.pillarFull[p][lang]}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-200">{noneOf(p) ? ms.actionsNone[p][lang] : ms.actions[p][lang]}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <button
            onClick={onBook}
            data-testid="to-booking-button"
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-teal-500 py-4 text-sm font-bold uppercase tracking-widest text-[#052E2B] transition-all duration-200 hover:bg-teal-300 hover:shadow-[0_0_30px_rgba(45,212,191,0.4)]"
          >
            {ms.analysis.toBooking[lang]} <ArrowRight size={16} />
          </button>

          <div className="mt-3" data-testid="deep-analysis-block">
            <button
              type="button"
              disabled
              data-testid="deep-analysis-button"
              className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full border border-teal-400/25 bg-teal-400/5 py-4 text-sm font-bold uppercase tracking-widest text-slate-400"
            >
              <Lock size={15} />
              {ms.analysis.deepCta[lang]}
              <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-amber-300">
                {ms.analysis.deepSoon[lang]}
              </span>
            </button>
            <p className="mt-2 text-center text-[11px] leading-relaxed text-slate-500" data-testid="deep-analysis-hint">{ms.analysis.deepHint[lang]}</p>
          </div>

          {/* --- EMAIL ME THE RESULT --- */}
          <div className="mg-glass mt-3 rounded-2xl p-6 sm:p-7" data-testid="results-email-card">
            <h3 className="font-heading flex items-center gap-2 text-lg font-bold text-white">
              <MailCheck size={18} className="text-teal-300" />
              {ms.analysis.emailHeading[lang]}
            </h3>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{ms.analysis.emailSub[lang]}</p>
            {mailState === "done" ? (
              <p className="mt-4 flex items-center gap-2 rounded-xl border border-teal-400/30 bg-teal-400/10 px-4 py-3.5 text-sm font-semibold text-teal-300" data-testid="results-email-success">
                <MailCheck size={16} /> {ms.analysis.emailDone[lang]}
              </p>
            ) : (
              <div className="mt-4 space-y-2.5">
                <input
                  value={mailName}
                  onChange={(e) => setMailName(e.target.value)}
                  placeholder={ms.analysis.emailNamePh[lang]}
                  className="w-full rounded-lg border border-teal-400/25 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors duration-200 focus:border-teal-300 focus:outline-none"
                  data-testid="results-email-name"
                />
                <div className="flex flex-col gap-2.5 sm:flex-row">
                  <input
                    type="email"
                    value={mailEmail}
                    onChange={(e) => setMailEmail(e.target.value)}
                    placeholder={ms.analysis.emailPh[lang]}
                    className="w-full rounded-lg border border-teal-400/25 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors duration-200 focus:border-teal-300 focus:outline-none"
                    data-testid="results-email-input"
                  />
                  <button
                    type="button"
                    onClick={sendResults}
                    disabled={!mailValid || mailState === "sending"}
                    data-testid="results-email-submit"
                    className="flex shrink-0 items-center justify-center gap-2 rounded-lg bg-teal-500 px-5 py-3 text-xs font-bold uppercase tracking-widest text-[#052E2B] transition-all duration-200 hover:bg-teal-300 hover:shadow-[0_0_20px_rgba(45,212,191,0.35)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Send size={14} />
                    {mailState === "sending" ? ms.analysis.emailSending[lang] : ms.analysis.emailBtn[lang]}
                  </button>
                </div>
                {mailState === "error" && (
                  <p className="font-mono text-xs font-bold uppercase tracking-widest text-red-400" data-testid="results-email-error">{ms.analysis.emailErr[lang]}</p>
                )}
              </div>
            )}
          </div>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              data-testid="back-to-radar-button"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-teal-400/30 py-3.5 text-xs font-bold uppercase tracking-widest text-teal-300 transition-colors duration-200 hover:bg-teal-400/10"
            >
              <ArrowLeft size={14} /> {ms.analysis.back[lang]}
            </button>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};
